import React, { useState, useEffect } from "react";
import { Card, Badge } from "react-bootstrap";
import "../ComponentsCss/AvoidableLossWidget.css";

const AvoidableLossWidget = ({ assets = [], currency = "USD" }) => {
  const [riskMitigationFactor, setRiskMitigationFactor] = useState(70);
  const [totalMetrics, setTotalMetrics] = useState({});
  
  const formatCurrency = (value) => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    
    // Format for compact display
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString(undefined, { 
      style: "currency", 
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    });
  };

  useEffect(() => {
    const metrics = assets.map((asset) => {
      const assetValue = Number(asset.assetValue) || 0;
      const exposureFactor = Number(asset.exposureFactor) || 50;
      const aro = Number(asset.annualizedRateOfOccurrence) || 0.2;
      
      // Core Risk Calculations from your calculator
      const sle = assetValue * (exposureFactor / 100);
      const ale = sle * aro;
      const avoidableLossWithMitigation = ale * (riskMitigationFactor / 100);
      const residualRisk = ale - avoidableLossWithMitigation;
      
      return {
        assetValue,
        sle,
        ale,
        avoidableLossWithMitigation,
        residualRisk,
      };
    });

    const totals = {
      totalAssetValue: metrics.reduce((sum, m) => sum + m.assetValue, 0),
      totalSLE: metrics.reduce((sum, m) => sum + m.sle, 0),
      totalALE: metrics.reduce((sum, m) => sum + m.ale, 0),
      totalAvoidableLoss: metrics.reduce((sum, m) => sum + m.avoidableLossWithMitigation, 0),
      totalResidualRisk: metrics.reduce((sum, m) => sum + m.residualRisk, 0)
    };

    setTotalMetrics(totals);
  }, [assets, riskMitigationFactor]);

  if (assets.length === 0) {
    return ( 
      <div className="alw-widget-container-v2">
       
          <Card.Body className="alw-card-body-v2">
            <div className="alw-header-v2">
                <h6 className="text-muted fw-semibold mb-1 text-uppercase avoidable" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Avoidable Loss</h6>
              <Badge className="alw-badge-empty-v2">N/A</Badge>
            </div>
            <div className="alw-empty-content-v2">
              <div className="alw-empty-icon">
                <i className="fas fa-chart-line fa-2x text-muted opacity-25"></i>
              </div>
              <p className="alw-empty-text-v2">No assets available</p>
              <small className="text-muted">Add assets to calculate loss</small>
            </div>
          </Card.Body>
       
      </div>
    );
  }

  return (   
    <div className="alw-widget-container-v2">
     
        <Card.Body className="alw-card-body-v2">
          {/* Header */}
          <div className="alw-header-v2">
              <h6 className="text-muted fw-semibold mb-1 text-uppercase avoidable" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Avoidable Loss</h6>
            <Badge className="alw-badge-v2">{riskMitigationFactor}%</Badge>
          </div>

          {/* Main Amount Display */}
          <div className="alw-main-display-v2">
            <h2 className="alw-amount-v2">{formatCurrency(totalMetrics.totalAvoidableLoss)}</h2>
            <p className="alw-subtitle-v2">With {riskMitigationFactor}% mitigation</p>
          </div>

          {/* Control Buttons */}
          <div className="alw-controls-v2">
            <label className="alw-control-label-v2">Risk Mitigation Effectiveness:</label>
            <div className="alw-button-group-v2">
              {[50, 70, 85].map(value => (
                <button
                  key={value}
                  type="button"
                  className={`alw-btn-v2 ${riskMitigationFactor === value ? 'alw-btn-active-v2' : 'alw-btn-outline-v2'}`}
                  onClick={() => setRiskMitigationFactor(value)}
                >
                  {value}%
                </button>
              ))}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="alw-stats-grid-v2">
            <div className="alw-stat-card-v2">
              <span className="alw-stat-label-v2">ANNUAL LOSS</span>
              <span className="alw-stat-value-v2 alw-danger">{formatCurrency(totalMetrics.totalALE)}</span>
            </div>
            <div className="alw-stat-card-v2">
              <span className="alw-stat-label-v2">RESIDUAL RISK</span>
              <span className="alw-stat-value-v2 alw-warning">{formatCurrency(totalMetrics.totalResidualRisk)}</span>
            </div>
          </div>

          {/* Bottom Insight */}
          <div className="alw-insight-v2">
            <div className="alw-progress-bar">
              <div 
                className="alw-progress-fill" 
                style={{ 
                  width: `${Math.min(((totalMetrics.totalAvoidableLoss / totalMetrics.totalALE) * 100), 100)}%` 
                }}
              ></div>
            </div>
            <span className="alw-insight-text-v2">
              <strong>{((totalMetrics.totalAvoidableLoss / totalMetrics.totalALE) * 100).toFixed(0)}% of risk mitigated</strong>
            </span>
          </div>
        </Card.Body>
     
    </div>
  );
};

export default AvoidableLossWidget;
